import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { usePicklistValuesAdmin, useTogglePicklistValue } from "@/hooks/usePicklistValues";
import { useUserProfile } from "@/hooks/useUserProfile";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "@/hooks/use-toast";

type UserRole = {
  role_id: string;
  role: Database["public"]["Tables"]["role"]["Row"] | null;
};

type UserProfile = Database["public"]["Tables"]["user_account"]["Row"] & {
  user_role: UserRole[];
};

// Categories shown in this UI, in display order.
const CATEGORIES: { key: string; label: string }[] = [
  { key: "empty_reason", label: "Out Reasons" },
];

function CategorySection({
  categoryKey,
  categoryLabel,
  canToggle,
}: {
  categoryKey: string;
  categoryLabel: string;
  canToggle: boolean;
}) {
  const [inactiveOpen, setInactiveOpen] = useState(false);
  const { data: values = [], isLoading } = usePicklistValuesAdmin(categoryKey);
  const toggleMutation = useTogglePicklistValue();

  const active = values.filter(v => v.is_active);
  const inactive = values.filter(v => !v.is_active);

  function handleToggle(id: string, currentlyActive: boolean) {
    toggleMutation.mutate(
      { id, is_active: !currentlyActive },
      {
        onSuccess: () =>
          toast({
            title: currentlyActive ? "Value deactivated" : "Value reactivated",
            description: currentlyActive
              ? "Hidden from all dropdowns. Existing data is unaffected."
              : "Restored to all dropdowns.",
          }),
        onError: (err: Error) =>
          toast({ title: "Update failed", description: err.message, variant: "destructive" }),
      }
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-foreground">{categoryLabel}</h3>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading…
        </div>
      ) : (
        <>
          {/* Active values */}
          <div className="rounded-md border divide-y">
            {active.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground italic">No active values.</p>
            )}
            {active.map(v => (
              <div key={v.id} className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-mono text-muted-foreground shrink-0">{v.value}</span>
                  <span className="text-sm truncate">{v.label}</span>
                  {v.bar_label && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 shrink-0">
                      {v.bar_label}
                    </Badge>
                  )}
                </div>
                {canToggle ? (
                  <Switch
                    checked={true}
                    disabled={toggleMutation.isPending}
                    onCheckedChange={() => handleToggle(v.id, true)}
                    aria-label={`Deactivate ${v.label}`}
                  />
                ) : (
                  <Badge variant="secondary" className="text-xs">Active</Badge>
                )}
              </div>
            ))}
          </div>

          {/* Inactive values — collapsible */}
          {inactive.length > 0 && (
            <div className="space-y-1">
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setInactiveOpen(o => !o)}
              >
                {inactiveOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                {inactive.length} inactive
              </button>

              {inactiveOpen && (
                <div className="rounded-md border divide-y bg-muted/30">
                  {inactive.map(v => (
                    <div key={v.id} className="flex items-center justify-between px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-mono text-muted-foreground shrink-0">{v.value}</span>
                        <span className="text-sm text-muted-foreground truncate">{v.label}</span>
                      </div>
                      {canToggle ? (
                        <Switch
                          checked={false}
                          disabled={toggleMutation.isPending}
                          onCheckedChange={() => handleToggle(v.id, false)}
                          aria-label={`Reactivate ${v.label}`}
                        />
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">Inactive</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function PicklistsTab() {
  const { data: profile } = useUserProfile();
  const typedProfile = profile as UserProfile | undefined;

  const isAdmin = typedProfile?.user_role?.some(ur => ur.role?.name === "admin") ?? false;
  const isManager = typedProfile?.user_role?.some(
    ur => ur.role?.name === "cellar_manager" || ur.role?.name === "manager"
  ) ?? false;

  const canToggle = isAdmin;
  const canView = isAdmin || isManager;

  if (!canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Picklists</CardTitle>
          <CardDescription>You don't have permission to view picklist settings.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Picklists</CardTitle>
          <CardDescription>
            {isAdmin
              ? "Control which values appear in dropdowns across the app. Toggle a value off to hide it — existing data using that value is never affected."
              : "View the values available in dropdowns across the app. Contact an admin to change visibility."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {CATEGORIES.map(cat => (
            <CategorySection
              key={cat.key}
              categoryKey={cat.key}
              categoryLabel={cat.label}
              canToggle={canToggle}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
